<template>
  <div class="input string optional">
    <div>
      <label v-if="addressLabel" :for="inputId">
        <strong>{{ addressLabel }}</strong>
        <span v-if="optionalLabel" style="font-weight: 400"> ({{ optionalLabel }})</span>
      </label>
      <input
        ref="autocomplete"
        class="location-for-copy input input--full input-max-length"
        maxlength="250"
        size="250"
        :placeholder="addressPlaceholder || t('type_an_adress')"
        type="text"
        v-model="location"
        :name="fieldLocation"
        :id="inputId"
      >
      <p v-if="addressHelper" class="section-heading__help">{{ addressHelper }}</p>
    </div>
    <div>
      <label v-if="captionLabel" :for="captionId">
        <strong>{{ captionLabel }}</strong>
        <span v-if="optionalLabel" style="font-weight: 400"> ({{ optionalLabel }})</span>
      </label>
      <textarea
        v-if="captionMultiline"
        ref="locationCaption"
        class="location-caption-for-copy input input--full input-max-length"
        maxlength="50"
        rows="1"
        style="resize: none; overflow: hidden"
        :placeholder="captionPlaceholder || t('display_name')"
        v-model="locationCaption"
        :name="fieldLocationCaption"
        :id="captionId"
      ></textarea>
      <input
        v-else
        ref="locationCaption"
        class="location-caption-for-copy input input--full input-max-length"
        maxlength="50"
        size="50"
        :placeholder="captionPlaceholder || t('display_name')"
        type="text"
        v-model="locationCaption"
        :name="fieldLocationCaption"
      >
      <p v-if="captionHelper" class="section-heading__help">{{ captionHelper }}</p>
    </div>
    <google-static-map :name="locationName" :lat="locationLat" :lng="locationLng"></google-static-map>
    <div v-for="(error, index) in locationErrors" :key="index" class="error">{{error}}</div>
    <input v-model="locationName" :name="fieldLocationName" type="hidden">
    <input v-model="locationLat" :name="fieldLocationLat" type="hidden">
    <input v-model="locationLng" :name="fieldLocationLng" type="hidden">
  </div>
</template>
<script>
import GoogleStaticMap from "./google_static_map";
import { shouldFillCaption } from "./location_caption";
import I18n from 'i18n-js/index.js.erb';
const t = (key, opts) => I18n.t(key, {
  scope: 'events.form_from_event',
  ...opts
});

const MIN_LOCATION_LENGTH = 2;

export default {
  props: {
    resourceName: String,
    initialLocation: String,
    initialLocationCaption: String,
    initialLocationName: String,
    initialLocationLat: String,
    initialLocationLng: String,
    errors: {
      type: String,
      default: "[]"
    },
    // Optional organizer-facing labels / placeholders / helper text. When
    // omitted (Event forms, wizard, copy dialogs) the component renders as
    // before: no labels, no helper text, and the legacy placeholders.
    addressLabel: {
      type: String,
      default: ""
    },
    addressPlaceholder: {
      type: String,
      default: ""
    },
    addressHelper: {
      type: String,
      default: ""
    },
    captionLabel: {
      type: String,
      default: ""
    },
    captionPlaceholder: {
      type: String,
      default: ""
    },
    captionHelper: {
      type: String,
      default: ""
    },
    // When true, a Google selection only fills the caption ("Location Name")
    // if it is currently blank, so an edited caption is never overwritten.
    // Passed as a string attribute from the Activity forms.
    fillCaptionWhenBlank: {
      type: [Boolean, String],
      default: false
    },
    // Regular-weight "(optional)" suffix rendered after the bold field name.
    optionalLabel: {
      type: String,
      default: ""
    },
    // When true, Location Name renders as an auto-growing <textarea>. Off by
    // default so Event forms, the wizard, and copy dialogs keep the <input>.
    multilineCaption: {
      type: [Boolean, String],
      default: false
    }
  },
  components: {
    GoogleStaticMap
  },
  data() {
    return {
      inputId: null,
      captionId: null,
      location: this.initialLocation,
      locationAddress: this.initialLocation,
      locationName: this.initialLocationName,
      locationCaption: this.initialLocationCaption,
      locationLat: this.initialLocationLat,
      locationLng: this.initialLocationLng,
      locationErrors: JSON.parse(this.errors)
    };
  },
  computed: {
    fieldLocation() {
      return `${this.resourceName}[location]`;
    },
    fieldLocationName() {
      return `${this.resourceName}[location_name]`;
    },
    fieldLocationCaption() {
      return `${this.resourceName}[location_caption]`;
    },
    fieldLocationLat() {
      return `${this.resourceName}[location_lat]`;
    },
    fieldLocationLng() {
      return `${this.resourceName}[location_lng]`;
    },
    // Custom-element attributes arrive as strings, so accept "true" as well.
    fillCaptionOnlyWhenBlank() {
      return this.fillCaptionWhenBlank === true || this.fillCaptionWhenBlank === "true";
    },
    captionMultiline() {
      return this.multilineCaption === true || this.multilineCaption === "true";
    }
  },
  methods: {
    t,
    handlePlaceChange(_name, placeResultData) {
      const { formatted_address, name, geometry } = placeResultData;
      this.location = this.$refs.autocomplete.value;
      this.locationAddress = formatted_address;
      this.locationName = name;
      if (shouldFillCaption({ fillWhenBlank: this.fillCaptionOnlyWhenBlank, currentCaption: this.locationCaption })) {
        this.locationCaption = name;
      }
      this.locationLat = geometry.location.lat();
      this.locationLng = geometry.location.lng();
      this.$nextTick(() => {
        $(this.$refs.autocomplete).change();
        $(this.$refs.locationCaption).change();
      });
    },
    initGeoComplete() {
      $(this.$refs.autocomplete).geocomplete({
        onChange: this.handlePlaceChange,
        geolocate: true,
        types: ["establishment", "geocode"],
        appendToParent: true
      });
    },
    // Location Name is a <textarea> so a long name wraps and the control grows
    // vertically instead of clipping. Resize to fit the current content.
    autoGrowCaption() {
      const el = this.$refs.locationCaption;
      if (!el || el.tagName !== "TEXTAREA") return;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  },
  watch: {
    location(newVal) {
      if (newVal.length <= MIN_LOCATION_LENGTH) {
        this.locationAddress = "";
        this.locationName = "";
        this.locationCaption = "";
        this.locationLat = "";
        this.locationLng = "";
        this.$nextTick(() => {
          $(this.$refs.autocomplete).change();
          $(this.$refs.locationCaption).change();
        });
      }
    },
    locationCaption() {
      this.$nextTick(this.autoGrowCaption);
    },
    initialLocation(newVal) {
      this.location = newVal;
      this.$nextTick(() => $(this.$refs.autocomplete).change());
    },
    initialLocationName(newVal) {
      this.locationName = newVal;
    },
    initialLocationCaption(newVal) {
      this.locationCaption = newVal;
      this.$nextTick(() => $(this.$refs.locationCaption).change());
    },
    initialLocationLat(newVal) {
      this.locationLat = newVal;
    },
    initialLocationLng(newVal) {
      this.locationLng = newVal;
    }
  },
  mounted() {
    this.inputId = "autocomplete-" + this._uid;
    this.captionId = "location-caption-" + this._uid;
    this.$nextTick(() => {
      this.initGeoComplete();
      this.autoGrowCaption();
    });
  }
};
</script>
